import React from 'react';
import I18n from 'i18n-js';
import { RCModal } from '@reverbdotcom/cadence/components';
import { useEppoAssignment, useUser } from '@reverbdotcom/commons/src/user_hooks';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { EppoExperimentValues, EppoSubjectTypes } from '@reverbdotcom/commons/src/eppo/eppoExperimentHelpers';

interface BumpModalShellProps {
  isOpen: boolean;
  onRequestClose: () => void;
  bid: number;
  showCustomWarning: boolean;
  onSubmit: () => void;
  onNoThankYou: () => void;
  children: React.ReactNode;
  showTopPickVariant?: boolean;
}

export function BumpModalShell({
  isOpen,
  onRequestClose,
  bid,
  showCustomWarning,
  onSubmit,
  onNoThankYou,
  children,
  showTopPickVariant = false,
}: BumpModalShellProps) {
  const user = useUser();
  const eligibleForExperiment = (!showTopPickVariant && isOpen && user?.id);
  const socialProofTreatmentGroup = useEppoAssignment({
    flagKey: eppoFlags.BUMP_SOCIAL_PROOF,
    forceReturnValue: eligibleForExperiment ? null : EppoExperimentValues.Control,
    subjectType: EppoSubjectTypes.USER_ID,
    subjectKey: user?.id,
  });
  const socialProofTreatmentEnabled = ['treatment-1', 'treatment-2'].includes(socialProofTreatmentGroup);
  const isCancelDismiss = bid > 0;
  const cancelButtonText = isCancelDismiss
    ? I18n.t('discovery.bumpModal.cancel')
    : I18n.t('discovery.bumpModal.noThankYou');

  return (
    <RCModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onRequestClose();
      }}
      title={socialProofTreatmentEnabled ? I18n.t('discovery.bumpModal.socialProof.title') : I18n.t('discovery.bumpModal.title')}
      actions={{
        primary: {
          buttonText: I18n.t('discovery.bumpModal.saveAndApply'),
          onClick: onSubmit,
          disabled: showCustomWarning,
        },
        secondary: {
          buttonText: cancelButtonText,
          onClick: isCancelDismiss ? onRequestClose : onNoThankYou,
        },
      }}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<{ socialProofTreatmentGroup?: string }>(child)) {
          return React.cloneElement(child, {
            socialProofTreatmentGroup: socialProofTreatmentGroup,
          });
        }
        return child;
      })}
    </RCModal >
  );
}
